import React from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    Card,
    CardHeader,
    IconButton
} from '@mui/material';
import { IdentityOemMapping } from 'common/types/Entities';

import { UserProfileCardHeaderCustomTitle } from './UserProfileCardHeaderCustomTitle';
import { UserProfileIdentitiesCardContentTemplate } from './UserProfileIdentitiesCardContentTemplate';

const BILLING_ACCOUNT_TOOLTIP_INFO = 'The status listed is for the user\'s association with the Billing Account, which may not reflect the status of the Billing account itself.';

const CARD_WRAPPER_INLINE_STYLES = {
    border: 1,
    borderColor: 'info.dark',
    borderRadius: '4px',
    bgcolor: 'secondary.light',
    width: 'calc(50% - 12px)',
    marginBottom: '24px',
    boxShadow: 0
};

enum UserProfileCardFields {
    FIRST_NAME = 'First Name',
    LAST_NAME = 'Last Name',
    COMPANY_KEY = 'Company Key',
    EMAIL = 'Email',
    USER_KEY = 'User Key',
    ACTIVE_BILLING_ACCOUNTS = 'Active Billing Accounts',
    INACTIVE_BILLING_ACCOUNTS = 'Inactive Billing Accounts',
    DELETED_BILLING_ACCOUNTS = 'Deleted Billing Accounts',
    USER_IDP_STATUS = 'User IdP Status'
}

interface UserProfileIdentitiesCardProps {
    userProfileInfo: IdentityOemMapping;
    onActionMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
}

export function UserProfileIdentititesCard({
    userProfileInfo,
    onActionMenuClick
}: Readonly<UserProfileIdentitiesCardProps>) {
    const {
        oemIdPName,
        firstName,
        lastName,
        email,
        subject,
        billingInformation
    } = userProfileInfo;

    const subjectIsGuid = isGuid(subject);
    const dowJonesIsIdP = oemIdPName.includes('Dow Jones');
    const activeBillingAccounts = getFilteredBillingAccounts(billingInformation, 'A');
    const inactiveBillingAccounts = getFilteredBillingAccounts(billingInformation, 'I');
    const deletedBillingAccounts = getFilteredBillingAccounts(billingInformation, 'D');
    const userIdpStatus = calculateUserIdpStatus(subjectIsGuid, dowJonesIsIdP, billingInformation);

    return (
        <Card sx={CARD_WRAPPER_INLINE_STYLES}>
            <CardHeader
                action={
                    <IconButton aria-label="settings" onClick={onActionMenuClick}>
                        <MoreVertIcon />
                    </IconButton>
                }
                title={<UserProfileCardHeaderCustomTitle name={oemIdPName}/>}
            />
            {renderUserInfo(firstName, lastName, email)}
            {dowJonesIsIdP && subjectIsGuid && renderBillingInfo(activeBillingAccounts, inactiveBillingAccounts, deletedBillingAccounts, userIdpStatus)}
            {subject && (
                <UserProfileIdentitiesCardContentTemplate
                    content={subject}
                    title={UserProfileCardFields.USER_KEY}
                    useCopyIcon
                />
            )}
        </Card>
    );
}

// Helper functions
function isGuid(str: string) {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return guidRegex.test(str);
}

function renderUserInfo(firstName: string, lastName: string, email: string) {
    return (
        <>
            {firstName && <UserProfileIdentitiesCardContentTemplate content={firstName} title={UserProfileCardFields.FIRST_NAME} />}
            {lastName && <UserProfileIdentitiesCardContentTemplate content={lastName} title={UserProfileCardFields.LAST_NAME} />}
            {email && <UserProfileIdentitiesCardContentTemplate content={email} title={UserProfileCardFields.EMAIL} />}
        </>
    );
}

function renderBillingInfo(activeBillingAccounts: string, inactiveBillingAccounts: string, deletedBillingAccounts: string, userIdpStatus: string) {
    return (
        <>
            <UserProfileIdentitiesCardContentTemplate
                content={activeBillingAccounts || '⚠ None'}
                title={UserProfileCardFields.ACTIVE_BILLING_ACCOUNTS}
            />
            {inactiveBillingAccounts && (
                <UserProfileIdentitiesCardContentTemplate
                    content={inactiveBillingAccounts}
                    description={BILLING_ACCOUNT_TOOLTIP_INFO}
                    title={UserProfileCardFields.INACTIVE_BILLING_ACCOUNTS}
                />
            )}
            {deletedBillingAccounts && (
                <UserProfileIdentitiesCardContentTemplate
                    content={deletedBillingAccounts}
                    description={BILLING_ACCOUNT_TOOLTIP_INFO}
                    title={UserProfileCardFields.DELETED_BILLING_ACCOUNTS}
                />
            )}
            <UserProfileIdentitiesCardContentTemplate
                content={userIdpStatus}
                title={UserProfileCardFields.USER_IDP_STATUS}
            />
        </>
    );
}

function getFilteredBillingAccounts(
    billingInformation: IdentityOemMapping['billingInformation'],
    status: string
): string {
    if (!billingInformation) return '';
    return billingInformation
        .filter(info => info.user_status === status)
        .map(info => info.account_id)
        .join(', ');
}

function calculateUserIdpStatus(
    subjectIsGuid: boolean,
    dowJonesIsIdP: boolean,
    billingInformation: IdentityOemMapping['billingInformation']
): string {
    if (!subjectIsGuid || !dowJonesIsIdP || !billingInformation || billingInformation.length === 0) {
        return 'Unknown';
    }

    const hasActiveEid = billingInformation.some((info) => info.user_status === 'A');
    const hasEnabledEmailLogin = billingInformation.some((info) => info.email_login_status === 'enabled');
    const hasInactiveEid = billingInformation.some((info) => info.user_status === 'I');
    const allEidsDeleted = billingInformation.every((info) => info.user_status === 'D');

    if (hasActiveEid && hasEnabledEmailLogin) {
        return 'Active';
    } else if (hasActiveEid && !hasEnabledEmailLogin) {
        return 'Login Disabled';
    } else if (!hasActiveEid && hasInactiveEid) {
        return 'Inactive';
    } else if (allEidsDeleted) {
        return 'Terminated';
    } else {
        return 'Unknown';
    }
}
